import * as RecxChat from 'components/ChatWidget';
import * as constants from 'constant/chat';

import { useCallback, useContext, useEffect, useRef } from 'react';
import { BOT_SESSION_ID } from 'constant/chat';
import useSessionStorage from 'hooks/useSessionStorage';
import { getUUID } from 'utils';
import useQuickActions from './useQuickActions';
import { ChatbotContext } from 'providers/ChatbotProvider';
import { IBotDetail, Intent } from 'interface';
import { isSurveyLinked, isSurveyFlowStart } from 'utils/chatUtils';
import useBotSession from './useBotSession';
import useLocalStorage from 'hooks/useLocalStorage';

const useChat = ({ botDetail }) => {
    const { setSessionStorageValue, clearSessionStorageValue, replaceSessionStorageValue } = useSessionStorage();
    const { quickActions } = useQuickActions({ botDetail });
    const {
        showQuickActions,
        setShowQuickActions,
        setIsGeneralQuerySelected,
        setIntentActionBtnQuery,
        setBotControllers,
        setSessionChatHistory,
        InputEnabledChat,
        InputDisabledChat
    } = useContext(ChatbotContext);

    const { createNewBotSession } = useBotSession();
    const { getLocalStorageValue } = useLocalStorage();


    const isChatEndRef = useRef(false);

    const {
        websites,
        documents,
        apis,
        botSettings: { isIntentFlow, isSmallTalk, disclaimer }
    } = botDetail as IBotDetail;

    useEffect(() => {
        const actions = quickActions?.map((action: string) => {
            const matchingApi = apis.find((api: Intent) => api.intentDetail.name === action);
            const isProminent = matchingApi?.intentDetail.isProminent || false;

            const className = isProminent ? 'custom-quick-button' : '';
            return { label: action, value: action, className: className };
        });

        RecxChat.setQuickButtons(showQuickActions && isIntentFlow ? actions : []);
        RecxChat.setDisclaimer(disclaimer);
    }, [quickActions, showQuickActions]);

    const setupChat = useCallback(() => {
        if (window.location.pathname === '/') {
            RecxChat.toggleWidget();
        }
        RecxChat.dropMessages();
        setSessionStorageValue(BOT_SESSION_ID, getUUID());
    }, [setSessionStorageValue]);

    const toggleChatLoader = () => RecxChat.toggleMsgLoader();

    const cleanupChat = () => {
        RecxChat.toggleWidget();
        clearSessionStorageValue(BOT_SESSION_ID);
    };

    const resetChat = () => {
        RecxChat.dropMessages();
        replaceSessionStorageValue(BOT_SESSION_ID, getUUID());
    };

    const handleReset = async () => {
        RecxChat.dropMessages();
        setShowQuickActions(true);
        setIsGeneralQuerySelected(false);
        isChatEndRef.current = false;

        setIntentActionBtnQuery(null);

        setBotControllers({
            website: websites.length > 0,
            document: documents.length > 0,
            api: apis.length > 0,
            small_talk: isSmallTalk,
            agentFacing: true
        });

        // setSessionChatHistory([getGreetingMessage(defaultGreetingMessage)]);

        const botSessionIdToRetain = getLocalStorageValue(constants.BOT_SESSION_ID);

        if (!isSurveyLinked(botDetail) || !isSurveyFlowStart(botDetail)) {
            // No need to create a new bot session if bot is rendered on new tab from clicking any link on previous chat
            if (!botSessionIdToRetain) {
                createNewBotSession().then(({ botSessionId }) => {
                    setSessionStorageValue(constants.BOT_SESSION_ID, botSessionId);
                    InputEnabledChat();
                });
            }

            return;
        }

        // remove greeting message from chat history if survey is linked
        setSessionChatHistory((prev) => prev.splice(1));

        // Disable chat input throughout the survey flow
        InputDisabledChat();

        // Add survey session and handle survey execution
        //addSurveySession(toggleChatLoader, botDetail).then(handleSurveyExecution).finally(toggleChatLoader);
    };

    return {
        setupChat,
        cleanupChat,
        resetChat,
        toggleChatLoader,
        isChatEndRef,
        handleReset
    };
};

export default useChat;
